import React, { useEffect, useMemo, useRef, useState } from "react";
import i18n from "../../../language/i18n";
import {
  loadTypeTags,
  suggestTypeTags,
  TYPE_TAG_GROUPS,
  typeTagLabel,
  TypeTag,
  TypeTagSuggestion,
} from "../../../services/typeTagApi";
import TypeTagPropose from "./TypeTagPropose";

const MAX_TAGS = 12;

const matches = (tag: TypeTag, needle: string): boolean => {
  if (!needle) return true;
  const hay = [tag.nameEn, tag.nameHi || "", ...(tag.aliases || [])].join(" ").toLowerCase();
  return hay.includes(needle);
};

/**
 * Multi-select for a product's type tags, in four groups. The classification above stays the
 * official record; this only adds the extra types (katan + tanchoi + butidar). Renders nothing
 * when the server has tags off.
 */
const TypeTagPicker = ({
  value,
  onChange,
  onAvailable,
  suggestFrom,
}: {
  value: string[];
  onChange: (ids: string[]) => void;
  /** The product's name and sub-category: tags they mention are offered as suggestions to confirm. */
  suggestFrom?: { name?: string; subcategoryName?: string };
  /** Called once with whether tags are on, so the parent can hide its own wrapper. */
  onAvailable?: (available: boolean) => void;
}) => {
  const [tags, setTags] = useState<TypeTag[] | null | undefined>(undefined);
  const [query, setQuery] = useState("");
  const [suggestions, setSuggestions] = useState<TypeTagSuggestion[]>([]);
  const [dismissed, setDismissed] = useState<string[]>([]);
  const suggestSeq = useRef(0);
  const suggestName = (suggestFrom?.name || "").trim();
  const suggestSub = (suggestFrom?.subcategoryName || "").trim();

  useEffect(() => {
    let live = true;
    loadTypeTags().then((rows) => {
      if (!live) return;
      setTags(rows);
      onAvailable?.(!!rows);
    });
    return () => {
      live = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Ask what the name and sub-category mention once typing pauses; a slow answer never overwrites a newer one.
  useEffect(() => {
    if (!tags || suggestName.length < 3) {
      setSuggestions([]);
      return undefined;
    }
    const seq = ++suggestSeq.current;
    const timer = window.setTimeout(() => {
      suggestTypeTags(suggestName, suggestSub || undefined).then((rows) => {
        if (seq === suggestSeq.current) setSuggestions(rows);
      });
    }, 450);
    return () => window.clearTimeout(timer);
  }, [tags, suggestName, suggestSub]);

  const selected = useMemo(() => new Set(value || []), [value]);
  const needle = query.trim().toLowerCase();

  if (tags === undefined) {
    return <p className="ps-tags__state" role="status">Loading tags…</p>;
  }
  if (tags === null) {
    return null;
  }
  if (!tags.length) {
    return <p className="ps-tags__state">No tags are set up yet.</p>;
  }

  const toggle = (id: string) => {
    const next = new Set(selected);
    if (next.has(id)) {
      next.delete(id);
    } else {
      if (next.size >= MAX_TAGS) return;
      next.add(id);
    }
    onChange(Array.from(next));
  };

  const language = i18n.language;
  const atLimit = selected.size >= MAX_TAGS;
  const offered = suggestions.filter((s) => !selected.has(s.id) && !dismissed.includes(s.id));
  const visibleGroups = TYPE_TAG_GROUPS.map((g) => ({
    ...g,
    rows: tags.filter((t) => t.group === g.group && (matches(t, needle) || selected.has(t.id))),
  })).filter((g) => g.rows.length);

  return (
    <div className="ps-tags">
      {offered.length ? (
        <div className="ps-tags__suggest" role="group" aria-label="Suggested tags">
          <small>{atLimit ? `Suggested from the name. You have ${MAX_TAGS} tags, so remove one to add more.` : "Suggested from the name. Tap to add."}</small>
          <div className="ps-tags__chips">
            {offered.map((s) => (
              <span key={s.id} className="ps-tags__suggest-chip">
                <button
                  type="button"
                  className="ps-chip-btn ps-tags__chip"
                  disabled={atLimit}
                  onClick={() => onChange(Array.from(selected).concat(s.id))}
                >
                  + {typeTagLabel(s, language)}
                  {s.confidence === "PARTIAL" ? <small>maybe</small> : null}
                </button>
                <button
                  type="button"
                  className="ps-tags__dismiss"
                  aria-label={`Dismiss ${typeTagLabel(s, language)}`}
                  onClick={() => setDismissed((d) => [...d, s.id])}
                >
                  ×
                </button>
              </span>
            ))}
          </div>
        </div>
      ) : null}
      <input
        type="search"
        className="form-control ps-tags__search"
        placeholder="Search tags (katan, tanchoi, butidar…)"
        aria-label="Search type tags"
        value={query}
        onChange={(e) => setQuery(e.target.value)}
      />
      {needle && !tags.some((t) => matches(t, needle)) ? (
        <p className="ps-tags__state">No tag matches “{query}”.</p>
      ) : null}
      {visibleGroups.map((g) => (
          <fieldset key={g.group} className="ps-tags__group">
            <legend>{g.label}</legend>
            <div className="ps-tags__chips">
              {g.rows.map((tag) => {
                const on = selected.has(tag.id);
                const label = typeTagLabel(tag, language);
                return (
                  <button
                    key={tag.id}
                    type="button"
                    className={`ps-chip-btn ps-tags__chip${on ? " is-on" : ""}`}
                    aria-pressed={on}
                    onClick={() => toggle(tag.id)}
                  >
                    {label}
                    {tag.nameHi && label === tag.nameEn ? <small lang="hi">{tag.nameHi}</small> : null}
                  </button>
                );
              })}
            </div>
          </fieldset>
      ))}
      <p className="ps-tags__count">
        {selected.size} of {MAX_TAGS} tags
        {selected.size >= MAX_TAGS ? " · limit reached" : ""}
      </p>
      <TypeTagPropose />
    </div>
  );
};

export default TypeTagPicker;
